/**
 * What the ingester is pulling out of the document, while it pulls it out.
 *
 * Processing a policy is otherwise a progress bar and a wait: the user hands
 * over a PDF and gets told a number of chunks. These are the actual terms the
 * retrieval index is being built from, so a wrong document (or a scan that
 * OCR'd into noise) is obvious immediately, instead of surfacing weeks later
 * as an answer that cites nothing useful.
 *
 * Terms are counted in the browser from chunk text already on screen — no new
 * request, and nothing here is stored.
 *
 * @module components/regulation/document/ExtractedTerms
 */
import { useMemo } from "react";

import { cn } from "~/lib/utils";
import type { PolicyChunk } from "../../../state/policies";

/**
 * Words that carry no signal in a compliance document. Deliberately short:
 * over-filtering hides the vocabulary that makes a policy recognisable, and
 * the point of this panel is recognition.
 */
const STOPWORDS = new Set([
  "the", "and", "for", "that", "this", "with", "any", "all", "are", "not", "may",
  "shall", "will", "must", "such", "from", "has", "have", "been", "was", "were",
  "its", "their", "these", "those", "other", "into", "than", "then", "them",
  "there", "which", "who", "whom", "whose", "you", "your", "our", "his", "her",
  "she", "him", "they", "would", "could", "should", "about", "under", "over",
  "each", "also", "more", "most", "some", "only", "when", "where", "what",
  "how", "why", "per", "via", "upon", "within", "between", "including",
  "include", "includes", "used", "use", "using", "set", "one", "two", "three",
]);

export interface ExtractedTerm {
  readonly term: string;
  readonly count: number;
}

/**
 * The most frequent meaningful terms across the chunks extracted so far.
 *
 * Ties break alphabetically so the list does not reshuffle on every poll — a
 * panel that churns while nothing changed reads as broken.
 */
export function extractTerms(
  chunks: ReadonlyArray<Pick<PolicyChunk, "content">>,
  limit = 28,
): ReadonlyArray<ExtractedTerm> {
  const counts = new Map<string, number>();
  for (const chunk of chunks) {
    // Keep intra-word hyphens and apostrophes: "risk-based" and "issuer's" are
    // the vocabulary, not two fragments each.
    const words = chunk.content.toLowerCase().match(/[a-z][a-z'-]{2,}/g);
    if (!words) continue;
    for (const raw of words) {
      const word = raw.replace(/^[-']+|[-']+$/g, "");
      if (word.length < 3 || STOPWORDS.has(word)) continue;
      counts.set(word, (counts.get(word) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([term, count]) => ({ term, count }))
    .sort((left, right) =>
      right.count === left.count
        ? left.term.localeCompare(right.term)
        : right.count - left.count,
    )
    .slice(0, limit);
}

/** Four weights, so the eye ranks the vocabulary without reading any numbers. */
function weightClass(count: number, top: number): string {
  const share = top === 0 ? 0 : count / top;
  if (share > 0.66) return "bg-primary/15 text-primary text-sm";
  if (share > 0.33) return "bg-muted text-foreground text-sm";
  if (share > 0.15) return "bg-muted/70 text-muted-foreground text-xs";
  return "bg-muted/50 text-muted-foreground/80 text-xs";
}

export function ExtractedTerms({
  chunks,
  active,
  className,
}: {
  readonly chunks: ReadonlyArray<PolicyChunk>;
  /** True while the run is still producing chunks. */
  readonly active: boolean;
  readonly className?: string;
}) {
  const terms = useMemo(() => extractTerms(chunks), [chunks]);
  const top = terms[0]?.count ?? 0;

  return (
    <section
      className={cn("rounded-xl border border-border/60 bg-card p-4", className)}
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          {active ? "Reading the document" : "What this document is about"}
        </h3>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {chunks.length === 0
            ? "no text yet"
            : `${chunks.length} ${chunks.length === 1 ? "section" : "sections"}`}
        </span>
      </div>

      {terms.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {active
            ? "Terms appear here as each section is extracted."
            : "No text was extracted from this document. If it is a scan, it may need OCR before eve can answer from it."}
        </p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {terms.map((entry) => (
            <li
              key={entry.term}
              className={cn(
                "rounded-full px-2 py-0.5 leading-5",
                // A term that just arrived fades in; the rest stay put, so the
                // motion says "this is new" rather than "everything moved".
                "animate-in fade-in duration-300 motion-reduce:animate-none",
                weightClass(entry.count, top),
              )}
              title={`${entry.count} ${entry.count === 1 ? "mention" : "mentions"}`}
            >
              {entry.term}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
